import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import ActionButton from "../../components/common/ActionButton";
import { useStore } from "../../context/StoreContext";
import {
  useGetModifierGroupsQuery,
  useGetModifierOptionsQuery,
  useCreateModifierOptionMutation,
  useUpdateModifierOptionMutation,
  useDeleteModifierOptionMutation,
} from "../../store/api";

const ModifierOptions = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const { selectedStoreId } = useStore();

  const { data: groups } = useGetModifierGroupsQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });
  const {
    data: options,
    isLoading,
    isError,
    error,
  } = useGetModifierOptionsQuery(
    { storeId: selectedStoreId, groupId },
    { skip: !selectedStoreId || !groupId },
  );

  const [createOption, createOptionState] = useCreateModifierOptionMutation();
  const [updateOption, updateOptionState] = useUpdateModifierOptionMutation();
  const [deleteOption] = useDeleteModifierOptionMutation();

  const getInitialOptionForm = () => ({
    name: "",
    priceDelta: 0,
    sort: 0,
    isActive: true,
    linkedItemId: "",
    affectsInventory: false,
    defaultQty: 0,
    maxQty: 0,
    allowQuantity: false,
  });

  const [optionForm, setOptionForm] = useState(getInitialOptionForm);
  const [editingOptionId, setEditingOptionId] = useState(null);
  const [showOptionForm, setShowOptionForm] = useState(false);

  const groupsList = Array.isArray(groups) ? groups : [];
  const optionsList = Array.isArray(options) ? options : [];
  const selectedGroup = groupsList.find(
    (group) => (group.modifierGroupId || group.id) === groupId,
  );
  const selectedGroupName =
    selectedGroup?.name || selectedGroup?.modifierGroupName || "Selected Group";

  const handleOptionChange = (e) => {
    const { name, value } = e.target;
    setOptionForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleOptionCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setOptionForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleOptionSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStoreId || !groupId) {
      return;
    }
    try {
      const payload = {
        ...optionForm,
        priceDelta: Number(optionForm.priceDelta || 0),
        sort: Number(optionForm.sort || 0),
        defaultQty: Number(optionForm.defaultQty || 0),
        maxQty: Number(optionForm.maxQty || 0),
        linkedItemId: optionForm.linkedItemId || null,
      };
      if (editingOptionId) {
        await updateOption({
          storeId: selectedStoreId,
          groupId,
          optionId: editingOptionId,
          payload,
        }).unwrap();
      } else {
        await createOption({
          storeId: selectedStoreId,
          groupId,
          payload,
        }).unwrap();
      }
      setEditingOptionId(null);
      setOptionForm(getInitialOptionForm());
      setShowOptionForm(false);
    } catch (err) {
      // error handled by UI
    }
  };

  const handleAddOption = () => {
    setEditingOptionId(null);
    setOptionForm(getInitialOptionForm());
    setShowOptionForm(true);
  };

  const handleEditOption = (option) => {
    setEditingOptionId(option.modifierOptionId || option.id);
    setOptionForm({
      name: option.name || option.optionName || "",
      priceDelta: option.priceDelta ?? option.price ?? 0,
      sort: option.sort ?? 0,
      isActive: option.isActive !== undefined ? option.isActive : true,
      linkedItemId: option.linkedItemId || "",
      affectsInventory:
        option.affectsInventory !== undefined ? option.affectsInventory : false,
      defaultQty: option.defaultQty ?? 0,
      maxQty: option.maxQty ?? 0,
      allowQuantity:
        option.allowQuantity !== undefined ? option.allowQuantity : false,
    });
    setShowOptionForm(true);
  };

  const handleDeleteOption = async (optionId) => {
    if (window.confirm("Are you sure you want to delete this option?")) {
      try {
        await deleteOption({
          storeId: selectedStoreId,
          groupId,
          optionId,
        }).unwrap();
      } catch (err) {
        // error handled by UI
      }
    }
  };

  const handleCancelOption = () => {
    setEditingOptionId(null);
    setOptionForm(getInitialOptionForm());
    setShowOptionForm(false);
  };

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Modifier Options"
          subtitle="Manage options for a modifier group."
          actions={
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate("/menu/modifiers")}
            >
              Back
            </Button>
          }
        />
        <Card>
          <Alert tone="warning">
            Please select a store to manage modifier options.
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Modifier Options"
        subtitle={`Manage options for ${selectedGroupName}.`}
        actions={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate("/menu/modifiers")}
          >
            Back
          </Button>
        }
      />

      {showOptionForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900">
            {editingOptionId ? "Edit Option" : "Add Option"}
          </h2>
          <form className="space-y-4" onSubmit={handleOptionSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Option Name"
                name="name"
                placeholder="Large"
                value={optionForm.name}
                onChange={handleOptionChange}
                required
              />
              <Input
                label="Additional Price"
                name="priceDelta"
                type="number"
                step="0.01"
                placeholder="2.00"
                value={optionForm.priceDelta}
                onChange={handleOptionChange}
                helper="Extra charge for this option"
              />
              <Input
                label="Sort Order"
                name="sort"
                type="number"
                step="1"
                placeholder="0"
                value={optionForm.sort}
                onChange={handleOptionChange}
                helper="Lower values appear first"
              />
              <Input
                label="Linked Item ID"
                name="linkedItemId"
                placeholder="Optional linked item UUID"
                value={optionForm.linkedItemId}
                onChange={handleOptionChange}
                helper="Leave blank when this option is standalone"
              />
              <Input
                label="Default Quantity"
                name="defaultQty"
                type="number"
                step="1"
                min="0"
                placeholder="0"
                value={optionForm.defaultQty}
                onChange={handleOptionChange}
              />
              <Input
                label="Max Quantity"
                name="maxQty"
                type="number"
                step="1"
                min="0"
                placeholder="0"
                value={optionForm.maxQty}
                onChange={handleOptionChange}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <Checkbox
                label="Active"
                name="isActive"
                checked={optionForm.isActive}
                onChange={handleOptionCheckboxChange}
                helper="Inactive options stay hidden from use"
              />
              <Checkbox
                label="Affects Inventory"
                name="affectsInventory"
                checked={optionForm.affectsInventory}
                onChange={handleOptionCheckboxChange}
                helper="Use when this option impacts stock"
              />
              <Checkbox
                label="Allow Quantity"
                name="allowQuantity"
                checked={optionForm.allowQuantity}
                onChange={handleOptionCheckboxChange}
                helper="Enable quantity changes for this option"
              />
            </div>

            {(createOptionState.isSuccess || updateOptionState.isSuccess) && (
              <Alert tone="success">
                Option {editingOptionId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createOptionState.isError || updateOptionState.isError) && (
              <Alert tone="error">
                {createOptionState.error?.data?.detail ||
                  updateOptionState.error?.data?.detail ||
                  "Failed to save option."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={
                  createOptionState.isLoading || updateOptionState.isLoading
                }
              >
                {editingOptionId ? "Update" : "Add"} Option
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancelOption}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showOptionForm && (
        <Card className="p-0">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                Options for {selectedGroupName}
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Total options: {optionsList.length}
              </p>
            </div>
            <div className="flex gap-3">
              <Button size="sm" onClick={handleAddOption}>
                Add Option
              </Button>
            </div>
          </div>
          <div className="px-6 py-4">
            {isLoading && <p className="text-sm text-slate-500">Loading options...</p>}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load options."}
              </Alert>
            )}
            {!isLoading && !isError && optionsList.length === 0 && (
              <p className="text-sm text-slate-500">
                No options yet. Use Add Option to create one.
              </p>
            )}
            {!isLoading && !isError && optionsList.length > 0 && (
              <div className="space-y-2">
                {optionsList.map((option) => {
                  const optionId = option.modifierOptionId || option.id;
                  return (
                    <div
                      key={optionId}
                      className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-4 py-3"
                    >
                      <div>
                        <span className="font-medium text-slate-900">
                          {option.name || option.optionName}
                        </span>
                        {Number(option.priceDelta ?? option.price ?? 0) > 0 && (
                          <span className="ml-2 text-sm text-slate-600">
                            +${Number(
                              option.priceDelta ?? option.price ?? 0,
                            ).toFixed(2)}
                          </span>
                        )}
                        <div className="mt-1 text-xs text-slate-500">
                          Status: {option.isActive ? "Active" : "Inactive"}
                          {option.allowQuantity
                            ? ` | Qty ${option.defaultQty ?? 0}-${option.maxQty ?? 0}`
                            : ""}
                          {option.affectsInventory ? " | Tracks inventory" : ""}
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <ActionButton
                          label="Edit"
                          icon="edit"
                          onClick={() => handleEditOption(option)}
                        />
                        <ActionButton
                          label="Delete"
                          icon="delete"
                          tone="danger"
                          onClick={() => handleDeleteOption(optionId)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default ModifierOptions;
